import api from "../config/api";
import type { ApiResponse, PaginatedResponse } from "../types";

// ==================== TYPES ====================

export interface Attendance {
  _id: string;
  userId: {
    _id: string;
    name: string;
    employeeId: string;
    role: string;
    startWorkTime?: string;
    endWorkTime?: string;
  };
  date: string;
  checkInTime: string;
  checkOutTime?: string;
  actualWorkHours: number;
  lateMinutes: number;
  status: 'present' | 'late' | 'absent' | 'half-day' | 'on-leave';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AttendanceStats {
  totalDays: number;
  presentDays: number;
  absentDays: number;
  lateDays: number;
  totalLateMinutes: number;
  totalWorkHours: number;
}

export interface DailyReportSummary {
  total: number;
  present: number;
  late: number;
  absent: number;
  onLeave: number;
}

export interface DailyReport {
  date: string;
  summary: DailyReportSummary;
  attendances: Attendance[];
}

export interface MonthlyStatsResponse {
  employee: {
    _id: string;
    name: string;
    employeeId: string;
    role: string;
  };
  year: number;
  month: number;
  stats: AttendanceStats;
}

// ==================== REQUEST INTERFACES ====================

export interface CheckInRequest {
  notes?: string;
}

export interface CheckOutRequest {
  notes?: string;
}

export interface CreateManualAttendanceRequest {
  userId: string;
  date: string;
  checkInTime: string;
  checkOutTime?: string;
  status?: 'present' | 'late' | 'absent' | 'half-day' | 'on-leave';
  notes?: string;
}

export interface UpdateAttendanceRequest {
  checkInTime?: string;
  checkOutTime?: string;
  status?: 'present' | 'late' | 'absent' | 'half-day' | 'on-leave';
  notes?: string;
}

export interface GetAttendanceHistoryParams {
  userId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface GetMonthlyStatsParams {
  userId?: string;
}

export interface GetDailyReportParams {
  date?: string;
}

// ==================== ATTENDANCE SERVICE ====================

export const attendanceService = {
  /**
   * Check-in (ບັນທຶກເວລາເຂົ້າວຽກ)
   * @param data - Check-in data
   * @returns Attendance record
   */
  checkIn: async (data: CheckInRequest = {}): Promise<ApiResponse<Attendance>> => {
    const response = await api.post('/attendance/check-in', data);
    return response.data;
  },

  /**
   * Check-out (ບັນທຶກເວລາເລີກວຽກ)
   * @param data - Check-out data
   * @returns Attendance record
   */
  checkOut: async (data: CheckOutRequest = {}): Promise<ApiResponse<Attendance>> => {
    const response = await api.post('/attendance/check-out', data);
    return response.data;
  },

  /**
   * ເບິ່ງບັນທຶກວັນນີ້
   * @returns Today's attendance record
   */
  getTodayAttendance: async (): Promise<ApiResponse<Attendance | null>> => {
    const response = await api.get('/attendance/today');
    return response.data;
  },

  /**
   * ເບິ່ງປະຫວັດການເຂົ້າວຽກ
   * @param params - Query parameters
   * @returns Paginated attendance history
   */
  getAttendanceHistory: async (
    params: GetAttendanceHistoryParams = {}
  ): Promise<PaginatedResponse<Attendance>> => {
    const response = await api.get('/attendance/history', { params });
    return response.data;
  },

  /**
   * ສະຖິຕິປະຈຳເດືອນ
   * @param year - Year
   * @param month - Month (1-12)
   * @param params - Additional parameters
   * @returns Monthly statistics
   */
  getMonthlyStats: async (
    year: number,
    month: number,
    params: GetMonthlyStatsParams = {}
  ): Promise<ApiResponse<MonthlyStatsResponse>> => {
    const response = await api.get(`/attendance/stats/${year}/${month}`, { params });
    return response.data;
  },

  /**
   * ລາຍງານປະຈຳວັນ (Admin)
   * @param params - Query parameters
   * @returns Daily report
   */
  getDailyReport: async (
    params: GetDailyReportParams = {}
  ): Promise<ApiResponse<DailyReport>> => {
    const response = await api.get('/attendance/report/daily', { params });
    return response.data;
  },

  /**
   * ເບິ່ງລາຍລະອຽດບັນທຶກ
   * @param id - Attendance ID
   * @returns Attendance details
   */
  getAttendanceById: async (id: string): Promise<ApiResponse<Attendance>> => {
    const response = await api.get(`/attendance/${id}`);
    return response.data;
  },

  /**
   * ສ້າງບັນທຶກແບບ Manual (Admin)
   * @param data - Attendance data
   * @returns Created attendance
   */
  createManualAttendance: async (
    data: CreateManualAttendanceRequest
  ): Promise<ApiResponse<Attendance>> => {
    const response = await api.post('/attendance/manual', data);
    return response.data;
  },

  /**
   * ແກ້ໄຂບັນທຶກ (Admin)
   * @param id - Attendance ID
   * @param data - Updated data
   * @returns Updated attendance
   */
  updateAttendance: async (
    id: string,
    data: UpdateAttendanceRequest
  ): Promise<ApiResponse<Attendance>> => {
    const response = await api.put(`/attendance/${id}`, data);
    return response.data;
  },

  /**
   * ລຶບບັນທຶກ (Admin)
   * @param id - Attendance ID
   * @returns Deleted message
   */
  deleteAttendance: async (id: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/attendance/${id}`);
    return response.data;
  },

  // ==================== HELPER METHODS ====================

  /**
   * ກວດສອບວ່າ Check-in ແລ້ວບໍ່
   * @returns Boolean
   */
  hasCheckedInToday: async (): Promise<boolean> => {
    try {
      const response = await attendanceService.getTodayAttendance();
      return response.success && response.data !== null;
    } catch (error) {
      return false;
    }
  },

  /**
   * ກວດສອບວ່າ Check-out ແລ້ວບໍ່
   * @returns Boolean
   */
  hasCheckedOutToday: async (): Promise<boolean> => {
    try {
      const response = await attendanceService.getTodayAttendance();
      return response.success && response.data?.checkOutTime !== undefined;
    } catch (error) {
      return false;
    }
  },

  /**
   * ດຶງສະຖິຕິເດືອນປະຈຸບັນ
   * @param userId - Optional user ID
   * @returns Current month statistics
   */
  getCurrentMonthStats: async (
    userId?: string
  ): Promise<ApiResponse<MonthlyStatsResponse>> => {
    const now = new Date();
    return attendanceService.getMonthlyStats(
      now.getFullYear(),
      now.getMonth() + 1,
      userId ? { userId } : {}
    );
  },

  /**
   * ດຶງປະຫວັດເດືອນນີ້
   * @param userId - Optional user ID
   * @returns This month's attendance history
   */
  getThisMonthHistory: async (userId?: string): Promise<PaginatedResponse<Attendance>> => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return attendanceService.getAttendanceHistory({
      userId,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      limit: 100,
    });
  },

  /**
   * ດຶງລາຍງານວັນນີ້
   * @returns Today's report
   */
  getTodayReport: async (): Promise<ApiResponse<DailyReport>> => {
    return attendanceService.getDailyReport({
      date: new Date().toISOString().split('T')[0],
    });
  },

  /**
   * ຄຳນວນເວລາເຮັດວຽກຈາກ check-in ແລະ check-out
   * @param checkInTime - Check-in time
   * @param checkOutTime - Check-out time
   * @param breakMinutes - Break time in minutes
   * @returns Work hours
   */
  calculateWorkHours: (
    checkInTime: string,
    checkOutTime: string,
    breakMinutes: number = 60
  ): number => {
    const checkIn = new Date(checkInTime);
    const checkOut = new Date(checkOutTime);
    const diffMs = checkOut.getTime() - checkIn.getTime();
    const diffMinutes = diffMs / (1000 * 60);
    const workMinutes = diffMinutes - breakMinutes;
    return Math.max(0, workMinutes / 60);
  },

  /**
   * Format ເວລາສາຍເປັນຂໍ້ຄວາມ
   * @param lateMinutes - Late minutes
   * @returns Formatted string
   */
  formatLateTime: (lateMinutes: number): string => {
    if (lateMinutes === 0) return 'ບໍ່ສາຍ';
    if (lateMinutes < 60) return `ສາຍ ${lateMinutes} ນາທີ`;
    const hours = Math.floor(lateMinutes / 60);
    const minutes = lateMinutes % 60;
    return `ສາຍ ${hours} ຊົ່ວໂມງ ${minutes} ນາທີ`;
  },

  /**
   * Format ສະຖານະເປັນຂໍ້ຄວາມພາສາລາວ
   * @param status - Status
   * @returns Formatted string
   */
  formatStatus: (status: Attendance['status']): string => {
    const statusMap = {
      present: 'ມາວຽກ',
      late: 'ມາສາຍ',
      absent: 'ຂາດວຽກ',
      'half-day': 'ມາເຄິ່ງວັນ',
      'on-leave': 'ລາ',
    };
    return statusMap[status] || status;
  },

  /**
   * ດຶງສີສະຖານະ (ສຳລັບ UI)
   * @param status - Status
   * @returns Color code
   */
  getStatusColor: (status: Attendance['status']): string => {
    const colorMap = {
      present: '#52c41a',   // green
      late: '#faad14',      // orange
      absent: '#ff4d4f',    // red
      'half-day': '#1890ff', // blue
      'on-leave': '#722ed1', // purple
    };
    return colorMap[status] || '#d9d9d9';
  },
};